import {
	IconCheck,
	IconDeviceMobile,
	IconEyeOff,
	IconMinus,
	IconShieldLock,
	IconX,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { cn } from "@/lib/utils";
import { MARKETING_SECTION_ANIM_BASE } from "@/utils/animation";
import { seo } from "@/utils/seo";

const faqs = [
	{
		question: "Is Temp Mail free?",
		answer:
			"Yes, temp-mail.org is free with no registration. The service is ad-supported — you will see advertisements while using the web interface. ineedatest.email is also free, and does not display ads.",
	},
	{
		question: "How long does a Temp Mail address last?",
		answer:
			"Temp Mail addresses typically last up to 24 hours, though the service offers a range from 10 minutes to 24 hours on some versions. ineedatest.email lets you set any expiry duration you want when you create the address — shorter for a quick OTP, longer if you are in the middle of a multi-step sign-up process.",
	},
	{
		question: "Does Temp Mail have a mobile app?",
		answer:
			"Yes — temp-mail.org offers native iOS and Android apps, which is unusual in the disposable email space. ineedatest.email is web-only with no native mobile app. If you want a dedicated app on your phone for managing temporary inboxes, Temp Mail has a real advantage here.",
	},
	{
		question: "Are Temp Mail addresses blocked by websites?",
		answer:
			"Temp Mail's domains are on most major blocklists. It is one of the most widely used disposable email services, which means its domains are among the first to get added to fraud-prevention and sign-up blocklists. ineedatest.email uses multiple domains that are less widely known and blocked less frequently — though no disposable service is universally accepted.",
	},
	{
		question: "Can I choose my own Temp Mail address?",
		answer:
			"Temp Mail assigns a random address by default. Some versions let you change the local part (the portion before the @), but the interface varies by version and domain. ineedatest.email lets you pick your own local part when you create an address, which is useful when you want something predictable for a specific test or sign-up flow.",
	},
];

const comparisonRows: {
	feature: string;
	ours: { state: "yes" | "no" | "partial"; note?: string };
	theirs: { state: "yes" | "no" | "partial"; note?: string };
}[] = [
	{
		feature: "Completely free",
		ours: { state: "yes", note: "No ads, no paid tier" },
		theirs: { state: "yes", note: "Ad-supported" },
	},
	{
		feature: "Account required",
		ours: { state: "no" },
		theirs: { state: "no" },
	},
	{
		feature: "Mobile app",
		ours: { state: "no", note: "Web only" },
		theirs: { state: "yes", note: "iOS and Android apps" },
	},
	{
		feature: "User-controlled expiry",
		ours: { state: "yes", note: "Any duration you set" },
		theirs: { state: "partial", note: "10 min – 24 hr range" },
	},
	{
		feature: "Choose your own local part",
		ours: { state: "yes", note: "Pick the part before @" },
		theirs: { state: "partial", note: "Random by default, change available" },
	},
	{
		feature: "Ad-free interface",
		ours: { state: "yes" },
		theirs: { state: "no", note: "Displays ads" },
	},
	{
		feature: "Outbound sending",
		ours: { state: "no", note: "Inbound-only by design" },
		theirs: { state: "no" },
	},
	{
		feature: "Attachment storage",
		ours: { state: "no", note: "Text and HTML bodies only" },
		theirs: { state: "yes" },
	},
	{
		feature: "Multiple domains",
		ours: { state: "yes" },
		theirs: { state: "yes", note: "10+ domains" },
	},
	{
		feature: "API access",
		ours: { state: "no" },
		theirs: { state: "no" },
	},
	{
		feature: "Blocked by major services",
		ours: { state: "partial", note: "Less common" },
		theirs: { state: "yes", note: "Very well-known, widely blocked" },
	},
	{
		feature: "No IP logging",
		ours: { state: "yes" },
		theirs: { state: "yes" },
	},
];

const switchReasons = [
	{
		icon: IconEyeOff,
		title: "No ads, no tracking pixels, no distractions",
		body: "Temp Mail is ad-supported. The web interface serves display advertising while you wait for your verification email to arrive. ineedatest.email has no advertising, no analytics tracking beyond what the server logs, and no monetization layer. The inbox is the product — there is nothing else competing for your attention on the page.",
	},
	{
		icon: IconShieldLock,
		title: "Domains that clear more blocklists",
		body: "Temp-mail.org is one of the largest and most-used disposable email services in the world. That popularity means its domains appear on virtually every major blocklist. Many SaaS sign-up forms, financial services, and e-commerce platforms reject @temp-mail.org addresses at the form level before the email is even sent. ineedatest.email's domains are newer and less widely catalogued — they get through more of the sign-up flows you actually care about.",
	},
	{
		icon: IconCheck,
		title: "Pick the address you want, not a random one",
		body: "Temp Mail generates a random address when you load the page. If you want something predictable — a consistent address for a multi-step flow, or just a local part you can remember — ineedatest.email lets you type in what you want. You choose the local part, pick a domain, set a timer, and the inbox is live at that exact address.",
	},
	{
		icon: IconDeviceMobile,
		title: "Clean web tool for the one-and-done use case",
		body: "Temp Mail's feature set has grown: apps, multiple domains, push notifications. That breadth is valuable if you want a persistent mobile inbox. If you just need to receive one OTP or confirmation on a desktop browser, right now, ineedatest.email's stripped-down web interface gets out of the way faster. No app to install, no account to remember, no ad to close first.",
	},
];

const honestyRows = [
	{
		feature: "Native mobile apps",
		body: "Temp Mail has dedicated iOS and Android apps with push notifications for new emails. ineedatest.email is web-only — there is no native app and no push notifications. If you regularly use throwaway inboxes from your phone and want a native experience, Temp Mail has a genuine, meaningful advantage here.",
	},
	{
		feature: "Attachment handling",
		body: "Temp Mail stores email attachments so you can download files from received messages. ineedatest.email drops all binary content — only the text and HTML body of an email is stored. If you are expecting a PDF, image, or any other attachment, ineedatest.email will not have it.",
	},
	{
		feature: "Years of scale and reliability",
		body: "Temp Mail has served millions of users for years and has the infrastructure track record to match. ineedatest.email is a new solo side project. Anyone relying on it for anything time-sensitive or mission-critical should weigh that difference. Temp Mail's uptime history and operational maturity are not things ineedatest.email can match yet.",
	},
];

function StateCell({
	state,
	note,
}: {
	state: "yes" | "no" | "partial";
	note?: string;
}) {
	return (
		<div className="flex flex-col gap-0.5">
			<span className="flex items-center gap-1.5">
				{state === "yes" && (
					<IconCheck className="size-4 shrink-0 text-green-600 dark:text-green-500" />
				)}
				{state === "no" && (
					<IconX className="size-4 shrink-0 text-destructive" />
				)}
				{state === "partial" && (
					<IconMinus className="size-4 shrink-0 text-amber-500" />
				)}
				<span className="text-xs font-medium text-foreground capitalize">
					{state === "partial" ? "limited" : state}
				</span>
			</span>
			{note && (
				<span className="text-xs text-muted-foreground leading-snug">
					{note}
				</span>
			)}
		</div>
	);
}

export const Route = createFileRoute("/_marketing/alternatives/temp-mail")({
	component: RouteComponent,
	head: () =>
		seo({
			title: "Temp Mail alternative — ineedatest.email",
			description:
				"Ad-free disposable inboxes with expiry you control and no random address assignment. See how ineedatest.email compares to Temp Mail.",
			keywords:
				"temp mail alternative, temp-mail alternatives, alternative to temp mail, disposable email, temporary email, no ads",
			type: "FAQPage",
			faqItems: faqs,
		}),
});

export function RouteComponent() {
	return (
		<MarketingShell>
			{/* Hero */}
			<section className="pb-12 pt-20 sm:pb-16 sm:pt-28">
				<MarketingContainer>
					<nav className="mb-6 text-xs text-muted-foreground">
						<a href="/" className="hover:text-foreground">
							ineedatest.email
						</a>
						<span className="mx-2">/</span>
						<span>Temp Mail alternative</span>
					</nav>
					<div className={cn("max-w-3xl", MARKETING_SECTION_ANIM_BASE)}>
						<p className="mb-3 text-sm font-medium uppercase tracking-wider text-primary">
							Temp Mail alternative
						</p>
						<h1 className="mb-4 text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
							Looking for a Temp Mail alternative?
						</h1>
						<p className="text-lg text-pretty text-muted-foreground sm:text-xl">
							Temp Mail assigns a random address and serves ads while you wait.
							ineedatest.email lets you pick your address, set your own expiry,
							and has no ads — free, no account required.
						</p>
					</div>
				</MarketingContainer>
			</section>

			{/* Comparison table */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="Comparison"
						heading="Best Temp Mail alternatives in 2026"
						description="A feature-by-feature look at ineedatest.email vs Temp Mail for the most common disposable-inbox jobs: receiving OTPs, signing up for trials, and staying out of your real inbox."
						className="mb-10 sm:mb-12"
					/>
					<div className="overflow-x-auto rounded-xl border border-border/60">
						<table className="w-full min-w-[560px] text-sm">
							<thead>
								<tr className="border-b border-border/60 bg-muted/30">
									<th className="py-3 pl-5 pr-4 text-left font-semibold text-foreground">
										Feature
									</th>
									<th className="px-4 py-3 text-left font-semibold text-primary">
										ineedatest.email
									</th>
									<th className="px-4 py-3 text-left font-semibold text-foreground">
										Temp Mail
									</th>
								</tr>
							</thead>
							<tbody>
								{comparisonRows.map((row, i) => (
									<tr
										key={row.feature}
										className={cn(
											"border-b border-border/40 last:border-0",
											i % 2 === 0 ? "bg-transparent" : "bg-muted/10",
										)}
									>
										<td className="py-3 pl-5 pr-4 font-medium text-foreground">
											{row.feature}
										</td>
										<td className="px-4 py-3">
											<StateCell {...row.ours} />
										</td>
										<td className="px-4 py-3">
											<StateCell {...row.theirs} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<p className="mt-3 text-xs text-muted-foreground">
						Temp Mail features sourced from their public site. Accurate as of
						May 2026.
					</p>
				</MarketingContainer>
			</section>

			{/* Why switch */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="Why switch"
						heading="When ineedatest.email fits better than Temp Mail"
						description="Temp Mail works fine for a quick throwaway address. Here is when the difference in approach matters."
						className="mb-10 sm:mb-12"
					/>
					<div className="grid gap-6 sm:gap-8 lg:grid-cols-2">
						{switchReasons.map((reason, index) => {
							const Icon = reason.icon;
							return (
								<div
									key={reason.title}
									className={cn(
										"flex gap-5 rounded-xl border border-border/80 bg-card/30 p-6 ring-1 ring-border/40 sm:p-8",
										MARKETING_SECTION_ANIM_BASE,
									)}
									style={{ animationDelay: `${index * 0.1}s` }}
								>
									<div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/40">
										<Icon className="size-5 text-primary" aria-hidden />
									</div>
									<div className="flex min-w-0 flex-col gap-2">
										<h3 className="text-base font-semibold tracking-tight text-foreground">
											{reason.title}
										</h3>
										<p className="text-sm text-pretty text-muted-foreground">
											{reason.body}
										</p>
									</div>
								</div>
							);
						})}
					</div>
				</MarketingContainer>
			</section>

			{/* Honesty section */}
			<section className="border-t border-border/50 bg-muted/15 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="Honest take"
						heading="Where Temp Mail still wins"
						description="Three real situations where Temp Mail is the right choice, not ineedatest.email."
						className="mb-10 sm:mb-12"
					/>
					<div className="grid gap-5 sm:gap-6 lg:grid-cols-3">
						{honestyRows.map((row) => (
							<div
								key={row.feature}
								className="rounded-xl border border-border/60 bg-card/30 p-6 ring-1 ring-border/30"
							>
								<h3 className="mb-2 text-sm font-semibold text-foreground">
									{row.feature}
								</h3>
								<p className="text-sm text-pretty text-muted-foreground">
									{row.body}
								</p>
							</div>
						))}
					</div>
				</MarketingContainer>
			</section>

			{/* FAQ */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="FAQ"
						heading="Questions about switching from Temp Mail"
						className="mb-10 sm:mb-12"
					/>
					<div className="divide-y divide-border/50">
						{faqs.map((faq) => (
							<div key={faq.question} className="py-6 first:pt-0 last:pb-0">
								<h3 className="mb-2 text-base font-semibold text-foreground">
									{faq.question}
								</h3>
								<p className="text-sm text-pretty text-muted-foreground leading-relaxed">
									{faq.answer}
								</p>
							</div>
						))}
					</div>
				</MarketingContainer>
			</section>

			{/* CTA + sibling links */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<p className="text-base font-semibold text-foreground">
								Try ineedatest.email — no account, no ads, no setup
							</p>
							<p className="mt-1 text-sm text-muted-foreground">
								Pick a domain, set a timer, copy the address. Done.
							</p>
						</div>
						<a
							href="/"
							className="shrink-0 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
						>
							Get a disposable inbox
						</a>
					</div>

					<div className="mt-12 border-t border-border/50 pt-8">
						<p className="mb-3 text-sm text-muted-foreground">
							See what ineedatest.email can do:{" "}
							<a
								href="/features"
								className="text-primary underline-offset-4 hover:underline"
							>
								product features
							</a>
						</p>
						<p className="mb-4 text-sm font-medium text-muted-foreground">
							Compare other disposable email services
						</p>
						<div className="flex flex-wrap gap-3">
							<a
								href="/alternatives/mailinator"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								Mailinator alternatives
							</a>
							<a
								href="/alternatives/guerrilla-mail"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								Guerrilla Mail alternatives
							</a>
							<a
								href="/alternatives/10minutemail"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								10MinuteMail alternatives
							</a>
						</div>
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
